<template>
  <Teleport to="body">
    <div class="fixed top-0 right-0 z-[9999] w-full max-w-[26rem] max-h-[100dvh] overflow-x-hidden overflow-y-auto overscroll-contain p-4 space-y-4 pointer-events-none print:hidden" aria-live="polite" aria-relevant="additions">
      <TransitionGroup
        enter-active-class="transform transition duration-300 ease-out"
        enter-from-class="translate-x-full opacity-0"
        enter-to-class="translate-x-0 opacity-100"
        leave-active-class="transform transition duration-200 ease-in"
        leave-from-class="translate-x-0 opacity-100"
        leave-to-class="translate-x-full opacity-0"
      >
        <div
          v-for="notification in notifications"
          :key="notification.id"
          :class="getNotificationClass(notification.type)"
          class="pointer-events-auto w-full rounded-xl shadow-2xl overflow-hidden"
        >
          <div class="p-4">
            <div class="flex items-start">
              <!-- Icon -->
              <div class="flex-shrink-0" v-html="getIcon(notification.type)"></div>

              <!-- Message -->
              <div class="ml-3 min-w-0 flex-1 pt-0.5">
                <p class="text-sm font-medium [overflow-wrap:anywhere]" v-html="notification.message"></p>
              </div>

              <!-- Close Button -->
              <div class="ml-4 flex-shrink-0 flex">
                <button
                  @click="removeNotification(notification.id)"
                  class="inline-flex rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  <span class="sr-only">關閉</span>
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Progress Bar -->
          <div class="h-1 bg-black bg-opacity-10">
            <div
              class="h-full transition-all duration-100 ease-linear"
              :class="getProgressClass(notification.type)"
              :style="{ width: `${notification.progress}%` }"
            ></div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { TransitionGroup } from 'vue'

const notifications = ref([])
let notificationId = 0

const getNotificationClass = (type) => {
  const baseClasses = 'backdrop-blur-sm bg-opacity-95'

  switch (type) {
    case 'notice':
      return `${baseClasses} bg-blue-50 border-l-4 border-blue-500 text-blue-800`
    case 'success':
      return `${baseClasses} bg-green-50 border-l-4 border-green-500 text-green-800`
    case 'error':
    case 'danger':
      return `${baseClasses} bg-red-50 border-l-4 border-red-500 text-red-800`
    case 'warning':
      return `${baseClasses} bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800`
    default:
      return `${baseClasses} bg-gray-50 border-l-4 border-gray-500 text-gray-800`
  }
}

const getProgressClass = (type) => {
  switch (type) {
    case 'notice':
      return 'bg-blue-500'
    case 'success':
      return 'bg-green-500'
    case 'error':
    case 'danger':
      return 'bg-red-500'
    case 'warning':
      return 'bg-yellow-500'
    default:
      return 'bg-gray-500'
  }
}

const getIcon = (type) => {
  switch (type) {
    case 'notice':
      return '<svg class="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>'
    case 'success':
      return '<svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>'
    case 'error':
    case 'danger':
      return '<svg class="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>'
    case 'warning':
      return '<svg class="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>'
    default:
      return '<svg class="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>'
  }
}

const addNotification = (type, message, duration = 5000) => {
  const id = notificationId++
  const notification = {
    id,
    type,
    message,
    progress: 100
  }

  notifications.value.push(notification)

  // Progress bar animation
  const startTime = Date.now()
  const interval = setInterval(() => {
    const elapsed = Date.now() - startTime
    const remaining = Math.max(0, 100 - (elapsed / duration) * 100)

    const notif = notifications.value.find(n => n.id === id)
    if (notif) {
      notif.progress = remaining
    }

    if (remaining === 0) {
      clearInterval(interval)
    }
  }, 50)

  // Auto remove after duration
  setTimeout(() => {
    removeNotification(id)
  }, duration)
}

const removeNotification = (id) => {
  const index = notifications.value.findIndex(n => n.id === id)
  if (index !== -1) {
    notifications.value.splice(index, 1)
  }
}

// Expose method to window for easy access from Rails
if (typeof window !== 'undefined') {
  window.showFlashNotification = addNotification
}

// Read flash messages from DOM on mount
onMounted(() => {
  const flashContainer = document.getElementById('flash-data')
  if (flashContainer) {
    const messages = JSON.parse(flashContainer.dataset.flash || '[]')
    messages.forEach(({ type, message }) => {
      addNotification(type, message)
    })
    // Remove flash container after reading
    flashContainer.remove()
  }
})

defineExpose({
  addNotification
})
</script>
